import React, { useEffect, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Card, Table, Form, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import { useQuery } from 'react-query';
import Pagination from 'react-js-pagination';
import { Get } from '@app/utils/httpProvider';
import { ACCOUNT_API, ACCOUNT_AUTHINFO_API } from '@app/utils/apiUrl';
import { createUrlWithParam } from '../../../../utils/helper';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import { objectShowNumber } from '../../../../utils/define';

const TraCuuCanBo = () => {
    const history = useHistory();
    const location = useLocation();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [localSearch, setLocalSearch] = useState('');

    // Đọc URL Parameter trực tiếp trong Render block theo mẫu ChuyenNganh.js
    const paramUrl = new URLSearchParams(location.search.substring(1));
    if (paramUrl.get('page') != null && parseInt(paramUrl.get('page'), 10) !== page) {
        setPage(parseInt(paramUrl.get('page'), 10));
    }
    if (paramUrl.get('size') != null && parseInt(paramUrl.get('size'), 10) !== pageSize) {
        setPageSize(parseInt(paramUrl.get('size'), 10));
    }
    if (paramUrl.get('search') != null) {
        const querySearch = paramUrl.get('search') === 'null' ? '' : paramUrl.get('search');
        if (querySearch !== search) {
            setSearch(querySearch);
            setLocalSearch(querySearch);
        }
    }

    const replaceHistory = (Page, Size, Search) => {
        history.replace(
            createUrlWithParam(URI_INTERNAL.TRA_CUU_CAN_BO_PATH, {
                page: Page,
                size: Size,
                search: Search || null
            })
        );
    };

    // Sử dụng useQuery, hỗ trợ tìm kiếm phân cách bằng dấu phẩy
    const { isLoading, data, refetch } = useQuery(
        ['traCuuCanBoData', search, page, pageSize],
        async () => {
            if (!search || !search.trim()) {
                return {
                    items: [],
                    totalRecords: 0
                };
            }

            // Tách từ khóa bằng dấu phẩy
            const keywords = search.split(',').map(k => k.trim()).filter(Boolean);
            if (keywords.length === 0) {
                return {
                    items: [],
                    totalRecords: 0
                };
            }

            replaceHistory(page, pageSize, search);

            // Gọi API song song cho từng từ khóa
            const results = await Promise.all(
                keywords.map(async (keyword) => {
                    const model = {
                        Keyword: keyword,
                        PageIndex: 1,
                        PageSize: 100 // Lấy tối đa 100 kết quả cho mỗi từ khóa để gộp
                    };
                    try {
                        const response = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_NOT_MENU, null, model);
                        const resData = response?.data || response;
                        return resData?.data || resData?.$values || (Array.isArray(resData) ? resData : []);
                    } catch (err) {
                        console.error(`Error searching keyword "${keyword}":`, err);
                        return [];
                    }
                })
            );

            // Gộp kết quả và loại bỏ trùng lặp theo ID
            const mergedItems = [];
            const seenIds = new Set();
            for (const list of results) {
                for (const item of list) {
                    if (item && item.id && !seenIds.has(item.id)) {
                        seenIds.add(item.id);
                        mergedItems.push(item);
                    }
                }
            }

            const total = mergedItems.length;

            // Thực hiện phân trang local trên client
            const startIndex = (page - 1) * pageSize;
            const paginatedItems = mergedItems.slice(startIndex, startIndex + pageSize);

            // Gọi API song song lấy chi tiết tài khoản và Đơn vị cho trang hiện tại
            const itemsWithDetails = await Promise.all(paginatedItems.map(async (item) => {
                let updatedItem = { ...item };

                // 1. Lấy thông tin tài khoản (Họ tên) thông qua ACCOUNT_AUTHINFO_API (trả trực tiếp hồ sơ phẳng)
                if (item.id && (!item.accountAuthInfo || !item.accountAuthInfo.firstName)) {
                    try {
                        const detailRes = await Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${item.id}`);
                        const detailData = detailRes?.data || detailRes;
                        if (detailData) {
                            updatedItem.accountAuthInfo = detailData;
                        }
                    } catch (detailErr) {
                        console.error(`Error loading detail for account ${item.id}:`, detailErr);
                    }
                }

                // 2. Lấy thông tin đơn vị công tác
                if (item.id) {
                    try {
                        const infoRes = await Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${item.id}`);
                        const infoData = infoRes?.data || infoRes;
                        const donVisList = infoData?.donVis || [];
                        const donViName = donVisList.length > 0 
                            ? donVisList.map(d => d.tenDonVi).join(', ') 
                            : (infoData?.donViNgoaiVien || 'N/A');
                        updatedItem.donViName = donViName;
                    } catch (infoErr) {
                        console.error(`Error loading AccountInfo for account ${item.id}:`, infoErr);
                        updatedItem.donViName = 'N/A';
                    }
                } else {
                    updatedItem.donViName = 'N/A';
                }

                return updatedItem;
            }));

            return {
                items: itemsWithDetails,
                totalRecords: total
            };
        },
        {
            keepPreviousData: true
        }
    );

    useEffect(() => {
        refetch();
    }, [pageSize, page, search]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, search);
    };

    const onChangeShowNumber = (e) => {
        const size = Number(e.target.value);
        setPageSize(size);
        setPage(1);
        replaceHistory(1, size, search);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(localSearch);
        replaceHistory(1, pageSize, localSearch);
    };

    // Helper hiển thị tên theo thứ tự LastName + MiddleName + FirstName chuẩn Việt Nam
    const getFullName = (item) => {
        const fName = item.firstName || item.accountAuthInfo?.firstName || '';
        const mName = item.middleName || item.accountAuthInfo?.middleName || '';
        const lName = item.lastName || item.accountAuthInfo?.lastName || '';
        
        if (fName || mName || lName) {
            return `${lName} ${mName} ${fName}`.replace(/\s+/g, ' ').trim();
        }

        if (item.fullName) return item.fullName;
        if (item.fullname) return item.fullname;
        return 'N/A';
    };

    const items = data?.items || [];
    const totalRecords = data?.totalRecords || 0;
    const hasSearchKeyword = !!(search && search.trim());

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: '#f4f6f9', minHeight: 'calc(100vh - 120px)' }}>
            <div className="row justify-content-center">
                <div className="col-12">
                    <Card style={{
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #1572e8 0%, #0c56b8 100%)',
                            padding: '24px 30px',
                            color: '#fff',
                            borderBottom: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <h3 className="m-0 font-weight-bold d-flex align-items-center" style={{ fontSize: '22px', letterSpacing: '0.5px' }}>
                                <i className="fas fa-id-card-alt mr-3" style={{ fontSize: '26px', opacity: '0.9' }}></i>
                                Tra cứu hồ sơ cán bộ
                            </h3>
                            <p className="m-0 mt-1" style={{ fontSize: '13px', opacity: '0.8' }}>
                                Tìm kiếm nhanh chóng thông tin tài khoản (Username) theo Họ và tên cán bộ
                            </p>
                        </div>

                        <Card.Body className="p-4">
                            <Form onSubmit={handleSearchSubmit} className="mb-4">
                                <Row className="align-items-center">
                                    <Col md={8} lg={6} className="mb-3 mb-md-0">
                                        <InputGroup style={{
                                            borderRadius: '30px',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
                                            border: '1px solid #ced4da',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            <Form.Control
                                                placeholder="Nhập tên cán bộ (phân tách nhiều tên bằng dấu phẩy)..."
                                                value={localSearch}
                                                onChange={(e) => setLocalSearch(e.target.value)}
                                                style={{
                                                    border: 'none',
                                                    paddingLeft: '20px',
                                                    height: '46px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <button 
                                                type="submit" 
                                                className="btn d-flex align-items-center justify-content-center"
                                                style={{
                                                    backgroundColor: '#1572e8',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '0 25px',
                                                    fontWeight: '600',
                                                    fontSize: '14px',
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <i className="fas fa-search mr-2"></i>Tìm kiếm
                                            </button>
                                        </InputGroup>
                                    </Col>

                                    {hasSearchKeyword && (
                                        <Col md={4} lg={3} className="ml-auto d-flex align-items-center justify-content-end">
                                            <Form.Label className="mr-3 mb-0 text-secondary" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                Hiển thị:
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={pageSize}
                                                onChange={onChangeShowNumber}
                                                style={{
                                                    width: '90px',
                                                    borderRadius: '8px',
                                                    height: '40px',
                                                    border: '1px solid #ced4da',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                                                }}
                                            >
                                                {objectShowNumber.map((item, idx) => (
                                                    <option key={idx} value={item.value}>
                                                        {item.text}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Col>
                                    )}
                                </Row>
                            </Form>

                            <div className="table-responsive" style={{ borderRadius: '12px', border: '1px solid #eef2f6' }}>
                                <Table hover className="m-0" style={{ borderCollapse: 'separate' }}>
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                        <tr>
                                            <th className="border-0 text-secondary py-3 px-4" style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: '700', width: '70px' }}>STT</th>
                                            <th className="border-0 text-secondary py-3 px-3" style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: '700' }}>Họ và tên</th>
                                            <th className="border-0 text-secondary py-3 px-3" style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: '700' }}>Đơn vị</th>
                                            <th className="border-0 text-secondary py-3 px-3" style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: '700' }}>Tài khoản</th>
                                            <th className="border-0 text-secondary py-3 px-3" style={{ fontSize: '13px', textTransform: 'uppercase', fontWeight: '700', width: '150px' }}>Trạng thái</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="sr-only">Đang tải...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : !hasSearchKeyword ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5 text-secondary">
                                                    <i className="fas fa-search mr-2" style={{ fontSize: '20px', color: '#1572e8' }}></i>
                                                    Vui lòng nhập tên cán bộ và nhấn nút Tìm kiếm để tra cứu thông tin.
                                                </td>
                                            </tr>
                                        ) : items.length > 0 ? (
                                            items.map((item, index) => (
                                                <tr key={item.id || index} style={{ transition: 'background-color 0.2s', cursor: 'default' }}>
                                                    <td className="py-3 px-4 align-middle text-secondary font-weight-bold">
                                                        {index + 1 + (page - 1) * pageSize}
                                                    </td>
                                                    <td className="py-3 px-3 align-middle font-weight-bold text-dark">
                                                        {getFullName(item)}
                                                    </td>
                                                    <td className="py-3 px-3 align-middle text-secondary" style={{ fontSize: '14px' }}>
                                                        {item.donViName || 'N/A'}
                                                    </td>
                                                    <td className="py-3 px-3 align-middle font-weight-bold" style={{ color: '#1572e8' }}>
                                                        <span className="p-2" style={{ backgroundColor: '#ebf4ff', borderRadius: '8px' }}>
                                                            {item.username}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3 align-middle">
                                                        {item.isActive ? (
                                                            <Badge variant="success" className="py-1 px-2" style={{ borderRadius: '30px', fontWeight: '600', fontSize: '11px' }}>
                                                                Đã kích hoạt
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="danger" className="py-1 px-2" style={{ borderRadius: '30px', fontWeight: '600', fontSize: '11px' }}>
                                                                Chưa kích hoạt
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5 text-secondary">
                                                    <i className="fas fa-box-open mr-2" style={{ fontSize: '20px' }}></i>
                                                    Không tìm thấy dữ liệu cán bộ phù hợp.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {hasSearchKeyword && !isLoading && totalRecords > pageSize && (
                                <div className="row mt-4">
                                    <div className="col-sm-12">
                                        <div className="dataTables_paginate paging_simple_numbers">
                                            <Pagination
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                innerClass="pagination pagination-sm no-margin pull-right"
                                                activePage={page}
                                                itemsCountPerPage={pageSize}
                                                totalItemsCount={totalRecords}
                                                pageRangeDisplayed={5}
                                                onChange={changePage}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default TraCuuCanBo;
